import { useMemo } from 'react';
import type { Product, OnboardingSystem } from '../pages/ProductMatrixNew';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface Level2PlatformData {
  platform_description: string;
  products: Product[];
}

interface TabGenerationParams {
  selectedPlatform: string | null;
  platformProducts: Product[];
  products: Product[];
  hardwareProducts: Product[];
  softwareProducts: Product[];
  unifiedVisibleCountries: Country[];
  level2Platforms: Record<string, Level2PlatformData>;
  lbm2Products: Product[];
  onboardingSystems: OnboardingSystem[];
  COUNTRIES: Country[];
  browseMode: boolean;
  selectedCountry: string | null;
}

interface Tab {
  id: string;
  label: string;
  count: number;
  description?: string;
}

/**
 * Custom hook for generating platform tabs
 * Extracted from ProductMatrixNew.tsx to reduce component complexity
 */
export const useTabGeneration = (params: TabGenerationParams): Tab[] => {
  const {
    selectedPlatform,
    platformProducts,
    products,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    lbm2Products,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry
  } = params;

  return useMemo(() => {
    // Helper function to get filtered product count based on country availability
    const getFilteredProductCount = (products: Product[], visibleCountries: Country[]) => {
      return products.filter(product => {
        if (browseMode) {
          // Browse mode: check if product is available in any of the visible countries
          return visibleCountries.some(country => product.countries[country.code] === true);
        } else {
          // Location-filtered mode: check if product is available in the selected country
          return selectedCountry && product.countries[selectedCountry] === true;
        }
      }).length;
    };


    const tabs: Tab[] = [];

    // Special case for Integrated Payments platform: Use vertical-based tabs, only for ISV Partner Integration category
    if (selectedPlatform === 'Integrated Payments') {
      // Group platform products by vertical, filtering only ISV Partner Integration products
      const verticalGroups: Record<string, Product[]> = {};
      platformProducts
        .filter(product => product.category === 'ISV Partner Integration')
        .forEach(product => {
          const vertical = product.vertical;
          // Only include products with valid verticals
          if (!vertical || vertical.trim() === '') {
            return; // Skip products without verticals
          }

          // Normalize vertical names for cleaner display
          let normalizedVertical = vertical;
          if (vertical === 'F&B') normalizedVertical = 'Food & Beverage';
          if (vertical === 'Auto Rental') normalizedVertical = 'Auto';
          if (vertical.startsWith('Ecommerce')) normalizedVertical = 'Ecommerce';
          if (vertical === 'Moto') normalizedVertical = 'MOTO';
          if (vertical.includes('Vending')) normalizedVertical = vertical.split('(')[0].trim(); // "Retail (Vending)" -> "Retail"

          if (!verticalGroups[normalizedVertical]) {
            verticalGroups[normalizedVertical] = [];
          }
          verticalGroups[normalizedVertical].push(product);
        });

      // Add "All" tab first showing all products
      const allProducts = platformProducts.filter(product => product.category === 'ISV Partner Integration');
      tabs.push({
        id: 'vertical-all',
        label: 'All',
        count: allProducts.length,
        description: 'All partner integration solutions'
      });

      // Add "Pay at the Table" tab showing only PATT products
      const pattProducts = platformProducts.filter(product => product.category === 'ISV Partner Integration' && product.patt && product.patt.trim() !== '');
      tabs.push({
        id: 'vertical-patt',
        label: 'Pay at the Table',
        count: pattProducts.length,
        description: 'Partners with Pay at the Table support (Shift4 Mobile)'
      });

      // Create tabs for each vertical, sorted by custom order
      const verticalOrder = ['Food & Beverage', 'Lodging', 'Retail', 'Auto', 'MOTO', 'Ecommerce', 'Gaming','Misc'];
      Object.entries(verticalGroups)
        .sort(([a], [b]) => {
          const indexA = verticalOrder.indexOf(a);
          const indexB = verticalOrder.indexOf(b);
          // If not in custom order, put at end
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .forEach(([vertical, products]) => {
          tabs.push({
            id: `vertical-${vertical.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}`,
            label: vertical,
            count: products.length,
            description: `${vertical} solutions and integrations`
          });
        });
    } else {
      // Standard tab logic for other platforms

      // Tab order: Product Modules → Devices → Payments → Integrations

      // 1. Special case: Supported Partners tab for Shift4 Mobile (appears first, loads by default)
      if (selectedPlatform === 'Shift4 Mobile') {
        // Filter all products where patt has a value (PATT, OATT, or PATT / OATT)
        const supportedPartners = products.filter(p => p.patt && p.patt.trim() !== '');
        tabs.push({
          id: 'supported-partners',
          label: 'Supported Partners',
          count: getFilteredProductCount(supportedPartners, unifiedVisibleCountries),
          description: 'Partners with Pay at the Table support'
        });
      }

      // 2. Product Modules tab (formerly Solutions) - exclude for Shift4 Payments and Shift4 Mobile
      if (selectedPlatform !== 'Shift4 Payments' && selectedPlatform !== 'Shift4 Mobile') {
        tabs.push({
          id: 'product-modules',
          label: 'Product Modules',
          count: getFilteredProductCount(softwareProducts, unifiedVisibleCountries)
        });
      }

      // 3. Devices tab (exclude for Shift4 Payments and LBM 2.0)
      if (selectedPlatform !== 'Shift4 Payments' && selectedPlatform !== 'LBM 2.0') {
        tabs.push({
          id: 'supported-hardware',
          label: 'Supported Hardware',
          count: getFilteredProductCount(hardwareProducts, unifiedVisibleCountries)
        });
      }

      // 3. Special case: Add "Supported Payments" tab for SkyTab, SkyTab Venue, Shift4 Solo, and Shift4 Mobile
      if (selectedPlatform === 'SkyTab' || selectedPlatform === 'SkyTab Venue' || selectedPlatform === 'Shift4 Solo' || selectedPlatform === 'Shift4 Mobile') {
        // Get payment methods from Shift4 Payments, excluding "Local Payment Methods"
        const paymentsData = level2Platforms['Shift4 Payments'];
        if (paymentsData) {
          const supportedPayments = paymentsData.products.filter(
            product => product.category !== 'Local Payment Methods'
          );
          tabs.push({
            id: 'supported-payments',
            label: 'Supported Payments',
            count: getFilteredProductCount(supportedPayments, COUNTRIES),
            description: 'Payment methods supported by this platform'
          });
        }
      }

      // 4. Payment Types tab (from Level 2 Payments platform) - HIDDEN, replaced by Supported Payments tab for SkyTab, Shift4 Solo, and Shift4 Mobile
      // Only show Payment Methods tab for platforms that don't have Supported Payments tab and not LBM 2.0
      if (selectedPlatform !== 'SkyTab' && selectedPlatform !== 'SkyTab Venue' && selectedPlatform !== 'Shift4 Solo' && selectedPlatform !== 'Shift4 Mobile' && selectedPlatform !== 'LBM 2.0') {
        console.log('Checking for Payments platform:', level2Platforms['Shift4 Payments']);
        console.log('All level2Platforms keys:', Object.keys(level2Platforms));
        if (level2Platforms['Shift4 Payments']) {
          // For Payment Methods, use all countries instead of filtering by availability
          const paymentsVisibleCountries = COUNTRIES;
          console.log('Adding Payment Methods tab with count:', getFilteredProductCount(level2Platforms['Shift4 Payments'].products, paymentsVisibleCountries));
          tabs.push({
            id: 'level2-payments',
            label: 'Payment Methods',
            count: getFilteredProductCount(level2Platforms['Shift4 Payments'].products, paymentsVisibleCountries),
            description: level2Platforms['Shift4 Payments'].platform_description
          });
        } else {
          console.log('No Payments platform found in level2Platforms');
        }
      }

      // 5. Integrations (Other Level 2 Platform tabs, excluding Payments and Integrated Payments for Shift4 Payments)
      Object.entries(level2Platforms).forEach(([platformName, platformData]) => {
        if (platformName !== 'Shift4 Payments') { // Skip Payments since it's already added as Payment Types
          // Exclude Integrated Payments for Shift4 Payments
          if (selectedPlatform === 'Shift4 Payments' && platformName === 'SkyTab Marketplace Integrations') {
            return; // Skip this tab
          }
          // Exclude SkyTab Marketplace and Payment Methods for Shift4 Solo, Shift4 Mobile, and SkyTab Venue
          if ((selectedPlatform === 'Shift4 Solo' || selectedPlatform === 'Shift4 Mobile' || selectedPlatform === 'SkyTab Venue') && (platformName === 'SkyTab Marketplace Integrations' || platformName === 'Shift4 Payments')) {
            return; // Skip these tabs
          }
          // Exclude SkyTab Marketplace for LBM 2.0
          if (selectedPlatform === 'LBM 2.0' && platformName === 'SkyTab Marketplace Integrations') {
            return; // Skip this tab
          }
          const filteredCount = getFilteredProductCount(platformData.products, unifiedVisibleCountries);
          tabs.push({
            id: `level2-${platformName.toLowerCase().replace(/\s+/g, '-')}`,
            label: platformName === 'SkyTab Marketplace Integrations' ? 'SkyTab Marketplace' : platformName,
            count: filteredCount,
            description: platformData.platform_description
          });
        }
      });

      // 6. Special case: Add LBM 2.0 tab to SkyTab platform - TEMPORARILY HIDDEN
      // if (selectedPlatform === 'SkyTab' && lbm2Products.length > 0) {
      //   tabs.push({
      //     id: 'level3-lbm-2.0',
      //     label: 'LBM 2.0',
      //     count: getFilteredProductCount(lbm2Products, unifiedVisibleCountries),
      //     icon: Building2,
      //     description: 'Advanced version of Lighthouse Business Manager with enhanced tools for restaurants.'
      //   });
      // }
    }

    // 7. Add Onboarding tab as LAST tab for specific platforms
    const platformsWithOnboarding = ['SkyTab', 'SkyTab Venue', 'Integrated Payments', 'Shift4 Mobile', 'Shift4 Solo'];
    if (selectedPlatform && platformsWithOnboarding.includes(selectedPlatform) && onboardingSystems.length > 0) {
      // Count unique countries in onboarding data
      const uniqueCountries = new Set(onboardingSystems.map(system => system.country));
      tabs.push({
        id: 'onboarding',
        label: 'Onboarding',
        count: uniqueCountries.size,
        description: 'Onboarding and operational systems by country'
      });
    }

    return tabs;
  }, [
    selectedPlatform,
    platformProducts,
    products,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    lbm2Products,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry
  ]);
};
